import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { OrganizationUnitRecord, PlatformAdminService, TenantRecord } from './platform-admin.service';

@Component({
  standalone: true,
  selector: 'ain-platform-tenant-detail',
  providers: [ConfirmationService],
  imports: [AinPageComponent, AinStateComponent, AinSearchAccordionComponent, FormsModule, TableModule, ButtonModule, DialogModule, ConfirmDialogModule, InputTextModule],
  template: `
    <ain-page [eyebrow]="t('platform.eyebrow')" [title]="t('platform.tenantDetail.title')" [description]="t('platform.tenantDetail.description')">
      <p-confirmDialog />
      <div page-actions>
        @if (isDraft(tenant()?.status ?? '')) {
          <button
            pButton
            type="button"
            severity="danger"
            [label]="t('platform.tenants.provision.discardDraft')"
            [disabled]="saving()"
            (click)="confirmDiscard()"></button>
        }
        <button pButton type="button" [label]="t('platform.organization.create')" (click)="openCreateDialog()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('platform.tenantDetail.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('platform.tenantDetail.unavailable')" [message]="error() ?? ''" />
      } @else if (tenant()) {
        <section class="tenant-summary">
          <div><strong>{{ t('columns.name') }}:</strong> {{ tenant()?.name }}</div>
          <div><strong>{{ t('platform.tenants.slug') }}:</strong> {{ tenant()?.slug }}</div>
          <div><strong>{{ t('platform.tenants.legalName') }}:</strong> {{ tenant()?.legalName }}</div>
          <div><strong>{{ t('columns.status') }}:</strong> {{ tenantStatus(tenant()?.status ?? '') }}</div>
        </section>

        <section class="tenant-localization">
          <h3>{{ t('platform.localizationDefaults.title') }}</h3>
          <div class="localization-grid">
            <label>
              <span>{{ t('platform.localizationDefaults.calendarSystem') }}</span>
              <select [(ngModel)]="localizationModel.defaultCalendarSystem">
                <option value="jalali">{{ t('localizationPreferences.calendar.jalali') }}</option>
                <option value="gregorian">{{ t('localizationPreferences.calendar.gregorian') }}</option>
              </select>
            </label>
            <label>
              <span>{{ t('platform.localizationDefaults.culture') }}</span>
              <input pInputText [(ngModel)]="localizationModel.defaultCulture" />
            </label>
            <label>
              <span>{{ t('platform.localizationDefaults.timeZone') }}</span>
              <input pInputText [(ngModel)]="localizationModel.defaultTimeZone" />
            </label>
            <div class="localization-actions">
              <button pButton type="button" [label]="t('common.save')" (click)="saveTenantLocalizationDefaults()" [disabled]="savingLocalization()"></button>
            </div>
          </div>
        </section>

        <ain-search-accordion [title]="t('platform.search.title')">
          <section class="search-grid">
            <input pInputText [(ngModel)]="filters.query" [placeholder]="t('platform.search.query')" />
            <select [(ngModel)]="filters.type">
              <option value="">{{ t('platform.search.anyType') }}</option>
              <option value="0">{{ t('platform.organization.type.holding') }}</option>
              <option value="1">{{ t('platform.organization.type.company') }}</option>
              <option value="2">{{ t('platform.organization.type.branch') }}</option>
              <option value="3">{{ t('platform.organization.type.department') }}</option>
              <option value="4">{{ t('platform.organization.type.team') }}</option>
            </select>
            <button pButton type="button" [label]="t('platform.search.apply')" (click)="reload()"></button>
            <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
          </section>
        </ain-search-accordion>

        <section>
          <h3>{{ t('platform.organization.title') }}</h3>
          <p-table [value]="filteredOrganizationUnits()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('platform.organization.name') }}</th>
                <th>{{ t('platform.organization.code') }}</th>
                <th>{{ t('platform.organization.type') }}</th>
                <th>{{ t('platform.organization.level') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('platform.organization.path') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.name }}</td>
                <td>{{ row.code }}</td>
                <td>{{ unitType(row.type) }}</td>
                <td>{{ row.level }}</td>
                <td>{{ unitStatus(row.status) }}</td>
                <td>{{ row.path }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <p-dialog [header]="t('platform.organization.create')" [visible]="createDialogVisible()" [modal]="true" [style]="{ width: '48rem', maxWidth: '95vw' }" (visibleChange)="createDialogVisible.set($event)">
          <div class="grid">
            <select [(ngModel)]="createModel.parentId">
              <option [ngValue]="null">{{ t('platform.organization.parent.none') }}</option>
              @for (item of organizationUnits(); track item.id) {
                <option [ngValue]="item.id">{{ item.name }} ({{ item.code }})</option>
              }
            </select>
            <select [(ngModel)]="createModel.type">
              <option [ngValue]="0">{{ t('platform.organization.type.holding') }}</option>
              <option [ngValue]="1">{{ t('platform.organization.type.company') }}</option>
              <option [ngValue]="2">{{ t('platform.organization.type.branch') }}</option>
              <option [ngValue]="3">{{ t('platform.organization.type.department') }}</option>
              <option [ngValue]="4">{{ t('platform.organization.type.team') }}</option>
            </select>
            <input pInputText [(ngModel)]="createModel.name" [placeholder]="t('platform.organization.name')" />
            <input pInputText [(ngModel)]="createModel.code" [placeholder]="t('platform.organization.code')" />
            <button pButton type="button" [label]="t('common.save')" (click)="createOrganizationUnit()" [disabled]="saving()"></button>
          </div>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .tenant-summary { display: grid; gap: 6px; margin-bottom: 18px; }
    .tenant-localization { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 18px; }
    .tenant-localization h3 { margin: 0 0 10px; }
    .localization-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; align-items: end; }
    .localization-grid label { display: grid; gap: 6px; font-size: 12px; color: #475569; }
    .localization-actions { display: flex; justify-content: flex-end; }
    .search-grid { display: grid; grid-template-columns: 2fr 1fr auto auto; gap: 8px; align-items: center; }
    .search-grid select { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; min-height: 40px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr auto; gap: 8px; }
    select { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; }
    @media (max-width: 1200px) { .grid, .search-grid { grid-template-columns: 1fr; } }
  `]
})
export class PlatformTenantDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(PlatformAdminService);
  private readonly localizationService = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);

  readonly tenant = signal<TenantRecord | null>(null);
  readonly organizationUnits = signal<OrganizationUnitRecord[]>([]);
  readonly filteredOrganizationUnits = computed(() => {
    const query = this.filters.query.trim().toLowerCase();
    const type = this.filters.type.trim();
    return this.organizationUnits().filter(row => {
      const matchesQuery = !query || row.name.toLowerCase().includes(query) || row.code.toLowerCase().includes(query);
      const matchesType = !type || `${row.type}` === type;
      return matchesQuery && matchesType;
    });
  });
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly savingLocalization = signal(false);
  readonly error = signal<string | null>(null);
  readonly createDialogVisible = signal(false);

  private tenantId = '';
  createModel = {
    parentId: null as string | null,
    type: 1,
    name: '',
    code: ''
  };
  filters = {
    query: '',
    type: ''
  };
  localizationModel: { defaultCulture: string; defaultTimeZone: string; defaultCalendarSystem: 'gregorian' | 'jalali' } = {
    defaultCulture: 'fa-IR',
    defaultTimeZone: 'Asia/Tehran',
    defaultCalendarSystem: 'jalali'
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set(this.t('platform.tenantDetail.errorInvalidId'));
      this.loading.set(false);
      return;
    }

    this.tenantId = id;
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);

    let completed = 0;
    const markDone = (): void => {
      completed += 1;
      if (completed === 2) {
        this.loading.set(false);
      }
    };

    this.api.getTenant(this.tenantId).subscribe({
      next: item => {
        this.tenant.set(item);
        this.localizationModel = {
          defaultCulture: item.defaultCulture,
          defaultTimeZone: item.defaultTimeZone,
          defaultCalendarSystem: this.normalizeCalendarSystem(item.defaultCalendarSystem)
        };
        markDone();
      },
      error: () => {
        this.error.set(this.t('platform.tenantDetail.errorLoadTenant'));
        this.loading.set(false);
      }
    });

    this.api.listOrganizationUnits(this.tenantId, {
      search: this.filters.query.trim(),
      type: this.filters.type.trim() || undefined,
      skip: 0,
      take: 300
    }).subscribe({
      next: rows => {
        this.organizationUnits.set(rows.items);
        markDone();
      },
      error: () => {
        this.error.set(this.t('platform.tenantDetail.errorLoadOrg'));
        this.loading.set(false);
      }
    });
  }

  createOrganizationUnit(): void {
    const payload = {
      parentId: this.createModel.parentId,
      type: this.createModel.type,
      name: this.createModel.name.trim(),
      code: this.createModel.code.trim(),
      sortOrder: 0
    };

    if (!payload.name || !payload.code) {
      return;
    }

    this.saving.set(true);
    this.api.createOrganizationUnit(this.tenantId, payload).subscribe({
      next: created => {
        this.organizationUnits.update(rows => [...rows, created]);
        this.createModel.name = '';
        this.createModel.code = '';
        this.createModel.parentId = null;
        this.createDialogVisible.set(false);
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('platform.organization.errorCreate'));
        this.saving.set(false);
      }
    });
  }

  openCreateDialog(): void {
    this.createDialogVisible.set(true);
  }

  saveTenantLocalizationDefaults(): void {
    const defaultCulture = this.localizationModel.defaultCulture.trim();
    const defaultTimeZone = this.localizationModel.defaultTimeZone.trim();
    if (!defaultCulture || !defaultTimeZone) {
      return;
    }

    this.savingLocalization.set(true);
    this.api.updateTenantLocalizationDefaults(this.tenantId, {
      defaultCulture,
      defaultTimeZone,
      defaultCalendarSystem: this.localizationModel.defaultCalendarSystem
    }).subscribe({
      next: tenant => {
        this.tenant.set(tenant);
        this.localizationModel = {
          defaultCulture: tenant.defaultCulture,
          defaultTimeZone: tenant.defaultTimeZone,
          defaultCalendarSystem: this.normalizeCalendarSystem(tenant.defaultCalendarSystem)
        };
        this.savingLocalization.set(false);
      },
      error: () => {
        this.error.set(this.t('common.saveFailed'));
        this.savingLocalization.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filters.query = '';
    this.filters.type = '';
    this.reload();
  }

  tenantStatus(value: number | string): string {
    if (typeof value === 'string') {
      return value;
    }

    const map: Record<number, string> = {
      0: this.t('platform.tenants.status.draft'),
      1: this.t('platform.tenants.status.active'),
      2: this.t('platform.tenants.status.suspended'),
      3: this.t('platform.tenants.status.deleted')
    };

    return map[value] ?? `${value}`;
  }

  isDraft(status: number | string): boolean {
    return status === 0 || String(status).toLowerCase() === 'draft';
  }

  confirmDiscard(): void {
    this.confirmation.confirm({
      header: this.t('platform.tenants.provision.discardDraft'),
      message: this.t('platform.tenants.provision.discardDraftConfirm'),
      accept: () => this.discardDraft()
    });
  }

  unitType(value: number | string): string {
    if (typeof value === 'string') {
      return value;
    }

    const map: Record<number, string> = {
      0: this.t('platform.organization.type.holding'),
      1: this.t('platform.organization.type.company'),
      2: this.t('platform.organization.type.branch'),
      3: this.t('platform.organization.type.department'),
      4: this.t('platform.organization.type.team')
    };

    return map[value] ?? `${value}`;
  }

  unitStatus(value: number | string): string {
    if (typeof value === 'string') {
      return value;
    }

    const map: Record<number, string> = {
      0: this.t('platform.organization.status.active'),
      1: this.t('platform.organization.status.suspended'),
      2: this.t('platform.organization.status.deleted')
    };

    return map[value] ?? `${value}`;
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  private discardDraft(): void {
    this.saving.set(true);
    this.api.discardDraftTenant(this.tenantId).subscribe({
      next: () => {
        void this.router.navigate(['/platform/tenants']);
      },
      error: () => {
        this.error.set(this.t('platform.tenants.provision.discardDraftFailed'));
        this.saving.set(false);
      }
    });
  }

  private normalizeCalendarSystem(value: unknown): 'gregorian' | 'jalali' {
    if (typeof value === 'number') {
      return value === 1 ? 'jalali' : 'gregorian';
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === '1' || normalized === 'jalali') {
        return 'jalali';
      }
      if (normalized === '0' || normalized === 'gregorian') {
        return 'gregorian';
      }
    }

    return 'jalali';
  }
}
